import { useEffect, useState } from "react";
import { Building2, Loader2, MessageSquare, TrendingUp } from "lucide-react";
import moment from "moment";
import { Button } from "@/components/ui/button";
import { usageApi } from "@/apis";
import type { UsageUserDetail } from "@/apis";

interface UserUsageDetailProps {
  appId: string;
  userId: string;
}

interface MetricCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
}

function MetricCard({ label, value, icon }: MetricCardProps) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="flex items-center gap-2">
        <div className="text-muted-foreground">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold">{value}</p>
        </div>
      </div>
    </div>
  );
}

export function UserUsageDetail({ appId, userId }: UserUsageDetailProps) {
  const [detail, setDetail] = useState<UsageUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const userDetail = await usageApi.getUserDetail(appId, userId);
      setDetail(userDetail);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load user usage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [appId, userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Get metrics from the detail response
  const companiesIngested = detail?.companies_count ?? 0;
  const simulationsRan = detail?.simulations_count ?? 0;
  const activeUsers = 1; // This is a single user view

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {detail ? (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">{detail.email || detail.user_id}</h2>
              <p className="text-sm text-muted-foreground">{detail.user_id}</p>
            </div>
            
            {/* Static User Metrics */}
            <div className="grid grid-cols-3 gap-2">
              <MetricCard
                label="Companies"
                value={companiesIngested}
                icon={<Building2 className="h-4 w-4" />}
              />
              <MetricCard
                label="Simulations"
                value={simulationsRan}
                icon={<TrendingUp className="h-4 w-4" />}
              />
              <MetricCard
                label="User"
                value={activeUsers}
                icon={<MessageSquare className="h-4 w-4" />}
              />
            </div>
          </div>

          {!detail.usage_available ? (
            <p className="mt-4 text-sm text-amber-700">
              {detail.usage_error || "Usage data unavailable"}
            </p>
          ) : detail.last_activity_at ? (
            <p className="mt-4 text-sm text-muted-foreground">
              Last activity: {moment(detail.last_activity_at).format("YYYY-MM-DD HH:mm")}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button variant="outline" onClick={() => void loadData()}>
          Refresh
        </Button>
      </div>
    </div>
  );
}
